import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/kit';
import { FormDialog, PickSelect } from '@/components/inputs';
import { CONTACT_STATUSES } from '@/shared/constants';
import { committed } from '@/shared/data';

function fromMessage(m) {
  return {
    Name: m?.Name ?? '',
    email_c: m?.email_c ?? '',
    message_c: m?.message_c ?? '',
    status_c: m?.status_c || 'New',
  };
}

/** Log or edit a contact-form message. */
export default function ContactDialog({ open, onOpenChange, message, onSaved }) {
  const editing = !!message;
  const [form, setForm] = useState(fromMessage(message));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(fromMessage(message));
  }, [open]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function submit() {
    if (!form.Name.trim()) {
      toast.error('Add who it is from');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email_c.trim())) {
      toast.error('Add a valid email address');
      return;
    }
    if (!form.message_c.trim()) {
      toast.error('Add the message');
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      email_c: form.email_c.trim().toLowerCase(),
      message_c: form.message_c.trim(),
      status_c: form.status_c,
    };
    setSaving(true);
    try {
      const res = editing
        ? await sdk.table('contact_messages_c').update({ Id: message.Id, ...fields })
        : await sdk.table('contact_messages_c').create(fields);
      const saved = committed(res, editing ? 'Message updated' : 'Message logged');
      if (!saved) return;
      onOpenChange(false);
      setForm(fromMessage(null));
      onSaved?.(saved);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit message' : 'Log a message'}
      description={editing ? undefined : 'For a question that came in somewhere other than the contact form.'}
      onSubmit={submit}
      saving={saving}
      submitLabel={editing ? 'Save changes' : 'Log message'}
      busyLabel={editing ? 'Saving…' : 'Logging…'}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="From" htmlFor="cm-name">
          <Input id="cm-name" value={form.Name} onChange={(e) => set('Name')(e.target.value)} placeholder="Jonah Reyes" />
        </Field>
        <Field label="Email" htmlFor="cm-email">
          <Input
            id="cm-email"
            type="email"
            value={form.email_c}
            onChange={(e) => set('email_c')(e.target.value)}
            placeholder="jonah@example.com"
          />
        </Field>
      </div>
      <Field label="Message" htmlFor="cm-message">
        <Textarea
          id="cm-message"
          rows={5}
          value={form.message_c}
          onChange={(e) => set('message_c')(e.target.value)}
          placeholder="Do you have a plan for three TVs in different houses?"
        />
      </Field>
      <Field label="Status" htmlFor="cm-status">
        <PickSelect id="cm-status" value={form.status_c} onChange={set('status_c')} values={CONTACT_STATUSES} placeholder="Choose a status" />
      </Field>
    </FormDialog>
  );
}
